import React, { useMemo } from "react";
import { View, Pressable, StyleSheet, ActivityIndicator } from "react-native";
import { Clock, CalendarDots, SealCheck } from "@phosphor-icons/react";

import CustomText from "../../common/general/CustomText/CustomText";
import CheckedBadge from "../../svgs/common/CheckedBadge";
import CustomProgressBar from "../../common/general/CustomProgressBar/CustomProgressBar";
import { ActivityState } from "../../../graphql/generated/graphql";

function AiTutorCardProgress({
  icon,
  title,
  isHovered,
  isComplete = false,
  onPress,
  onHoverIn,
  onHoverOut,
  totalObjectives,
  completedObjectives,
  dueDate,
  isReviewContentBeingGenerated,
  activityState,
  scheduledDate,
}) {
  const progressPercentage = totalObjectives > 0 ? (completedObjectives / totalObjectives) * 100 : 0;

  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  const formattedScheduledDateString = useMemo(() => {
    if (!scheduledDate) return "Locked until further notice";
    const dueDate = new Date(scheduledDate);

    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    return `Unlocks ${month}/${day}`;
  }, [scheduledDate]);

  // Helper function to format due date
  const formatDueDate = (dateString) => {
    if (!dateString) return null;

    const dueDate = new Date(dateString);
    const today = new Date();

    const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    return {
      text: `Due ${month}/${day}`,
      isOverdue: dueDateStart < todayStart,
    };
  };

  const dueDateInfo = formatDueDate(dueDate);

  if (activityState === ActivityState.Draft) {
    return null;
  }

  return (
    <>
      <Pressable
        onPress={isModuleLocked ? undefined : onPress}
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        accessibilityLabel={`Open ${title} card`}
        accessibilityRole="button"
        style={({ pressed, focused }) => [
          styles.lessonItem,
          isHovered && styles.lessonItemHovered,
          !isModuleLocked && focused && styles.lessonItemFocused,
          pressed && styles.lessonItemPressed,
          isModuleLocked && styles.lessonItemLocked,
        ]}
        disabled={isReviewContentBeingGenerated}>
        <View style={styles.lessonItemLeft}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {isReviewContentBeingGenerated ? (
              <>
                <ActivityIndicator size="small" style={{ marginRight: 10 }} color="#3E68FE" />
                <CustomText
                  style={isModuleLocked && styles.lessonItemTitleLocked}
                  text="Review content is being generated"
                  weight="bold"
                  aria-level="3"
                  role="heading"
                />
              </>
            ) : (
              <>
                {icon && <View style={{ marginRight: 10 }}>{icon}</View>}
                <CustomText
                  text={title}
                  weight="bold"
                  style={[styles.lessonItemTitle, isModuleLocked && styles.lessonItemTitleLocked]}
                  useTranslationText={false}
                  aria-level="3"
                  role="heading"
                />
              </>
            )}
          </View>

          {!isReviewContentBeingGenerated ? (
            <View style={styles.progressContainer}>
              <View style={styles.progressStatus}>
                <View style={styles.progressBar}>
                  <CustomProgressBar
                    ariaLabel="Course progress bar"
                    progress={progressPercentage}
                    color={isComplete ? "#22C55E" : "#3E68FE"}
                    style={isModuleLocked && styles.lessonItemTitleLocked}
                  />
                </View>
                <CustomText
                  text={`${Math.round(progressPercentage)}% Complete`}
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  size="xs"
                  style={isModuleLocked && styles.lessonItemTitleLocked}
                />
                {!isModuleLocked && dueDateInfo && (
                  <View style={[styles.dueDatePill, dueDateInfo.isOverdue && styles.overduePill]}>
                    <CalendarDots
                      size={14}
                      color={dueDateInfo.isOverdue ? "#DC2626" : "#344054"}
                      style={styles.calendarIcon}
                    />
                    <CustomText
                      weight="bold"
                      text={dueDateInfo.text}
                      size="s"
                      style={[styles.dueDateText, dueDateInfo.isOverdue && styles.overdueText]}
                      useTranslationText={false}
                    />
                  </View>
                )}
                {isModuleLocked && (
                  <View style={styles.progressContainer}>
                    <View style={styles.dueDatePill}>
                      <Clock size={14} color={"#344054"} style={styles.calendarIcon} />
                      <CustomText
                        weight="bold"
                        text={formattedScheduledDateString}
                        size="s"
                        style={styles.dueDateText}
                        useTranslationText={false}
                      />
                    </View>
                  </View>
                )}
              </View>
            </View>
          ) : (
            isModuleLocked && (
              <View style={styles.progressContainer}>
                <View style={styles.dueDatePill}>
                  <Clock size={14} color={"#344054"} style={styles.calendarIcon} />
                  <CustomText
                    weight="bold"
                    text={formattedScheduledDateString}
                    size="s"
                    style={styles.dueDateText}
                    useTranslationText={false}
                  />
                </View>
              </View>
            )
          )}
        </View>
        {!isReviewContentBeingGenerated && (isComplete ? <CheckedBadge /> : <SealCheck size={24} color="#525457" />)}
      </Pressable>
    </>
  );
}

const styles = StyleSheet.create({
  progressBar: {
    width: 150,
    maxWidth: 150,
  },
  lessonItem: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  lessonItemLocked: {
    cursor: "auto",
  },
  lessonItemHovered: {
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  lessonItemFocused: {
    outlineWidth: 2,
    outlineColor: "#3E68FE",
    outlineStyle: "solid",
    boxShadow: "0 0 0 2px rgba(62, 104, 254, 0.2)",
  },
  lessonItemPressed: {
    backgroundColor: "#F5F5F5",
  },
  lessonItemLeft: {
    flex: 1,
    marginRight: 16,
  },
  lessonItemTitle: {
    marginBottom: 4,
  },
  lessonItemTitleLocked: {
    opacity: 0.5,
  },
  progressContainer: {
    marginTop: 4,
    gap: 8,
  },
  progressStatus: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 12,
  },
  dueDatePill: {
    backgroundColor: "#EAECF0",
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 360,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  overduePill: {
    backgroundColor: "#FEE2E2",
  },
  calendarIcon: {
    marginRight: 2,
  },
  dueDateText: {
    color: "#667085",
    fontSize: 12,
    fontWeight: "500",
  },
  overdueText: {
    color: "#DC2626",
  },
});

export default AiTutorCardProgress;
